export type ShareVisibilityPricing = 'full' | 'total_only' | 'hidden';

export type ShareVisibility = {
	pricing: ShareVisibilityPricing;
	timeline: boolean;
	instructions: boolean;
	team: boolean;
};

export const DEFAULT_SHARE_VISIBILITY: ShareVisibility = {
	pricing: 'full',
	timeline: true,
	instructions: true,
	team: true
};

export const QUOTE_SHARE_VISIBILITY: ShareVisibility = {
	pricing: 'hidden',
	timeline: true,
	instructions: false,
	team: false
};

export function normalizeShareVisibility(input?: Partial<ShareVisibility> | null): ShareVisibility {
	if (!input) return { ...DEFAULT_SHARE_VISIBILITY };
	const pricing = input.pricing;
	const allowed: ShareVisibilityPricing[] = ['full', 'total_only', 'hidden'];
	return {
		pricing: allowed.includes(pricing as ShareVisibilityPricing) ? (pricing as ShareVisibilityPricing) : 'full',
		timeline: input.timeline !== false,
		instructions: input.instructions !== false,
		team: input.team !== false
	};
}

export function shareVisibilityShowsPricing(visibility: ShareVisibility): boolean {
	return visibility.pricing !== 'hidden';
}

export function shareVisibilityShowsPricingBreakdown(visibility: ShareVisibility): boolean {
	return visibility.pricing === 'full';
}

type CopyFn = (en: string, de: string, hr: string) => string;

/** Short label for cards / badges showing current client visibility. */
export function summarizeShareVisibility(visibility: ShareVisibility, copy: CopyFn): string {
	const v = normalizeShareVisibility(visibility);
	const hidden: string[] = [];

	if (v.pricing === 'hidden') {
		hidden.push(copy('pricing', 'Preise', 'cijene'));
	} else if (v.pricing === 'total_only') {
		return copy('Total estimate only', 'Nur Gesamtschätzung', 'Samo ukupna procjena');
	}
	if (!v.timeline) hidden.push(copy('timeline', 'Zeitplan', 'vremenski plan'));
	if (!v.instructions) hidden.push(copy('instructions', 'Anweisungen', 'upute'));
	if (!v.team) hidden.push(copy('team', 'Team', 'tim'));

	if (hidden.length === 0) {
		return copy('Full plan visible', 'Voller Plan sichtbar', 'Cijeli plan vidljiv');
	}
	return copy(
		`Hidden: ${hidden.join(', ')}`,
		`Ausgeblendet: ${hidden.join(', ')}`,
		`Skriveno: ${hidden.join(', ')}`
	);
}

export function inviteAllowsClientVisibilityEdit(status?: string | null): boolean {
	return status === 'pending' || status === 'used';
}

export function shareVisibilityIsRestricted(visibility?: ShareVisibility | null): boolean {
	const v = normalizeShareVisibility(visibility);
	return v.pricing !== 'full' || !v.timeline || !v.instructions || !v.team;
}
